/**
 * KanbanToolbar
 *
 * Toolbar do Kanban: filtros, busca e ações.
 * Mobile: hierarquia visual (ferramentas unificadas + contexto do funil + busca + filtros).
 * Desktop (md+): layout original intacto.
 */

import React from 'react';
import FilterBar from '../../../components/shared/filters/FilterBar';
import { Filters } from '../../../components/shared/filters/types';
import { StagesFilter } from '../../../components/shared/filters/StagesFilter';
import { QuickFilters, QuickFiltersState } from '../../../components/shared/filters/QuickFilters';
import { Funnel, FunnelStage } from '../../../types';
import { usePermissions } from '../../../hooks/usePermissions';
import { FunnelSelector } from './FunnelSelector';
import { ViewModeSwitch } from './ViewModeSwitch';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';

interface KanbanToolbarProps {
    viewMode: 'kanban' | 'list';
    onViewModeChange: (mode: 'kanban' | 'list') => void;
    onFilterChange: (filters: Filters) => void;
    onQuickFilterChange: (filters: QuickFiltersState) => void;
    onStageFilterChange: (stageId: string) => void;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onCreateLead: () => void;
    funnels: Funnel[];
    selectedFunnelId: string | null;
    onFunnelChange: (id: string) => void;
    onEditFunnel: () => void;
    onCreateFunnel: () => void;
    currentFilters: Filters;
    funnelStages: FunnelStage[];
}

const KanbanToolbar: React.FC<KanbanToolbarProps> = ({
    viewMode,
    onViewModeChange,
    onFilterChange,
    onQuickFilterChange,
    onStageFilterChange,
    searchTerm,
    onSearchChange,
    onCreateLead,
    funnels,
    selectedFunnelId,
    onFunnelChange,
    onEditFunnel,
    onCreateFunnel,
    currentFilters,
    funnelStages,
}) => {
    const { canCreateFunnels, canEditFunnels } = usePermissions();
    const canManageFunnels = canCreateFunnels || canEditFunnels;

    const quickFilterProps = {
        filters: {
            noAgent: !!currentFilters.noAgent,
            noTasks: !!currentFilters.noTasks,
            overdueTasks: !!currentFilters.overdueTasks,
            statusWon: !!currentFilters.statusWon,
            statusLost: !!currentFilters.statusLost,
        },
        onChange: onQuickFilterChange,
    };

    const funnelProps = {
        funnels,
        selectedFunnelId,
        onFunnelChange,
        onEditFunnel,
        onCreateFunnel,
        canManageFunnels,
    };

    return (
        <div className="min-w-0 w-full shrink-0">
            {/* ── Mobile ── */}
            <div className="flex min-w-0 w-full flex-col gap-s3 md:hidden">
                {/* 1. Contexto do funil — linha própria (evita esmagar as ferramentas) */}
                <FunnelSelector
                    {...funnelProps}
                    className="min-w-0 w-full"
                    triggerClassName="h-11 w-full max-w-full justify-between rounded-xl px-s3 touch-manipulation"
                />

                {/* 2. Ferramentas — faixa full-width (filtro | kanban | lista | etapas) */}
                <div
                    role="toolbar"
                    aria-label="Filtros rápidos e visualização"
                    className="grid h-11 w-full min-w-0 grid-cols-[1fr_2fr_1fr] items-stretch overflow-hidden rounded-xl border border-border bg-card divide-x divide-border"
                >
                    <QuickFilters
                        {...quickFilterProps}
                        embedded
                        triggerClassName="h-11 w-full min-w-0"
                    />
                    <ViewModeSwitch
                        viewMode={viewMode}
                        onViewModeChange={onViewModeChange}
                        embedded
                        className="min-w-0"
                    />
                    <StagesFilter
                        stages={funnelStages}
                        selectedStage={currentFilters.stage}
                        onSelect={onStageFilterChange}
                        embedded
                        triggerClassName="h-11 w-full min-w-0"
                    />
                </div>

                {/* 3. Busca */}
                <SearchInput
                    value={searchTerm}
                    onChange={onSearchChange}
                    className="min-h-11 w-full min-w-0"
                    placeholder="Buscar negociações..."
                />

                {/* 4. Filtros de dados */}
                <FilterBar
                    onFilterChange={onFilterChange}
                    selectedFunnelId={selectedFunnelId}
                    currentFilters={currentFilters as any}
                />
            </div>

            {/* ── Desktop (md+) ── */}
            <div className="hidden md:flex md:min-w-0 md:flex-row md:items-center md:gap-s2 md:overflow-x-auto no-scrollbar">
                <div className="w-auto">
                    <FunnelSelector {...funnelProps} />
                </div>

                <div className="flex-none">
                    <FilterBar
                        onFilterChange={onFilterChange}
                        selectedFunnelId={selectedFunnelId}
                        currentFilters={currentFilters as any}
                    />
                </div>

                <div className="flex-1" />

                <div className="w-[200px] shrink-0">
                    <SearchInput
                        value={searchTerm}
                        onChange={onSearchChange}
                        className="h-9"
                    />
                </div>

                <div className="flex items-center gap-s2 py-s2">
                    <Button onClick={onCreateLead} className="min-h-9 whitespace-nowrap touch-manipulation">
                        Nova Negociação
                    </Button>

                    <div className="ml-s1 flex items-center gap-s2 border-l border-subtle pl-s2">
                        <QuickFilters {...quickFilterProps} />
                        <ViewModeSwitch viewMode={viewMode} onViewModeChange={onViewModeChange} />
                        <StagesFilter
                            stages={funnelStages}
                            selectedStage={currentFilters.stage}
                            onSelect={onStageFilterChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KanbanToolbar;
